/**
 * Outlier score: server-authoritative and deterministic (docs/SPEC.md §9.1).
 *
 * baseline = median of the creator's own reels' metric over ALL their qualifying reels we have (decision 83,
 *            `window` = 0), or over the most recent `window` of them when `window` > 0; excluding pinned reels, reels younger than `minAgeMs`, reels without a posting time and
 *            reels whose metric is unknown or ≤ 0.
 * score    = metric / baseline, 2 dp, clamped to `clampMax`. NULL when the metric is unknown or the
 *            creator has fewer than `minSample` qualifying reels (25 by default, decision 71). Never defaults to 1.0.
 * basis    = 'views' if any own reel has views > 0, else 'likes' if any has visible likes > 0. One basis
 *            per creator, never mixed.
 */

export interface ScoreInput {
  pk: string;
  views: number | null;
  likes: number | null;
  likesHidden?: boolean;
  postedAt: number | null; // epoch ms
  isPinned: boolean;
}

export interface ScoreOptions {
  /** 0 = all qualifying reels (decision 83, the default); N > 0 = only the most recent N. */
  window: number;
  minSample: number;
  minAgeMs: number;
  clampMax: number;
}

export const SCORE_DEFAULTS: ScoreOptions = {
  // Owner decision 83 (2026-10-21): usual views = median of ALL the creator's qualifying reels. Admin `scores.window`.
  window: 0,
  // Owner decision 71 (2026-10-19): 25. Admin `scores.minSample` (must stay ≤ `scores.window` when the window is > 0).
  minSample: 25,
  minAgeMs: 72 * 60 * 60 * 1000,
  clampMax: 9999.99,
};

export type ScoreBasis = 'views' | 'likes';

export interface Baseline {
  basis: ScoreBasis | null;
  /** Median metric of the qualifying reels, or null when the sample is too small. */
  value: number | null;
  /** Number of reels used for the median. */
  n: number;
}

/**
 * The minimum sample the server applies: `minSample`, capped at the window when there is one (decision 71). With
 * window 0 (all reels, decision 83) there is no cap.
 */
export function effectiveMinSample(window: number, minSample: number): number {
  return window > 0 ? Math.min(minSample, window) : minSample;
}

const positive = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

export function chooseBasis(reels: ScoreInput[]): ScoreBasis | null {
  if (reels.some((r) => positive(r.views))) return 'views';
  if (reels.some((r) => !r.likesHidden && positive(r.likes))) return 'likes';
  return null;
}

export function metricOf(r: ScoreInput, basis: ScoreBasis): number | null {
  if (basis === 'views') return positive(r.views) ? r.views : null;
  return !r.likesHidden && positive(r.likes) ? r.likes : null;
}

export function computeBaseline(reels: ScoreInput[], now: number, opts: ScoreOptions = SCORE_DEFAULTS): Baseline {
  const basis = chooseBasis(reels);
  if (!basis) return { basis: null, value: null, n: 0 };
  const qualifying = reels
    .filter((r) => !r.isPinned && r.postedAt !== null && now - r.postedAt >= opts.minAgeMs && metricOf(r, basis) !== null)
    .sort((a, b) => b.postedAt! - a.postedAt!);
  // window 0 = every qualifying reel (decision 83); one in-memory pass over the rows recompute already read.
  if (opts.window > 0) qualifying.length = Math.min(qualifying.length, opts.window);
  const n = qualifying.length;
  if (n < opts.minSample) return { basis, value: null, n };
  return { basis, value: median(qualifying.map((r) => metricOf(r, basis)!)), n };
}

export function roundScore(x: number, clampMax = SCORE_DEFAULTS.clampMax): number {
  return Math.min(clampMax, Math.round(x * 100) / 100);
}

/** Scores every reel against one baseline. Returns pk → score (null when not scorable). */
export function scoreReels(reels: ScoreInput[], now: number, opts: ScoreOptions = SCORE_DEFAULTS): {
  baseline: Baseline;
  scores: Map<string, number | null>;
} {
  const baseline = computeBaseline(reels, now, opts);
  const scores = new Map<string, number | null>();
  for (const r of reels) {
    if (!baseline.basis || !baseline.value) {
      scores.set(r.pk, null);
      continue;
    }
    const m = metricOf(r, baseline.basis);
    scores.set(r.pk, m === null ? null : roundScore(m / baseline.value, opts.clampMax));
  }
  return { baseline, scores };
}

/**
 * One reel's Wise Score against ANOTHER creator's stored baseline (decision 71): a collab reel shown on a co-author's
 * Reels tab or report is scored against that co-author's usual views (or usual likes on a likes basis), computed at
 * read time. The reel never enters that creator's baseline. Null when the creator has no baseline or the metric is missing.
 */
export function scoreAgainstBaseline(
  r: Pick<ScoreInput, 'views' | 'likes' | 'likesHidden'>,
  basis: string | null | undefined,
  baselineValue: number | null | undefined,
  clampMax = SCORE_DEFAULTS.clampMax,
): number | null {
  if ((basis !== 'views' && basis !== 'likes') || !positive(baselineValue)) return null;
  const m = metricOf({ pk: '', postedAt: null, isPinned: false, ...r }, basis);
  return m === null ? null : roundScore(m / baselineValue, clampMax);
}

export type Tier = 'legendary' | 'viral' | 'outlier' | 'above' | 'normal';

export interface TierThresholds {
  legendary: number;
  viral: number;
  outlier: number;
  above: number;
}

export const TIER_DEFAULTS: TierThresholds = { legendary: 10, viral: 5, outlier: 3, above: 1.5 };

/**
 * What people see for each tier (SPEC §21 decision 62; the website uses the same names). The keys stay as they are:
 * the 3×+ tier is keyed `outlier` in code, settings and the API but is shown as "Hot".
 */
export const TIER_LABELS: Record<Tier, string> = { legendary: 'Legendary', viral: 'Viral', outlier: 'Hot', above: 'Above usual', normal: 'Normal' };

/** The score's display name everywhere a user, admin or AI app can see it (decision 62). */
export const SCORE_NAME = 'Wise Score';

export function tierFor(score: number | null, t: TierThresholds = TIER_DEFAULTS): Tier | null {
  if (score === null || !Number.isFinite(score)) return null;
  if (score >= t.legendary) return 'legendary';
  if (score >= t.viral) return 'viral';
  if (score >= t.outlier) return 'outlier';
  if (score >= t.above) return 'above';
  return 'normal';
}

/** One emoji per tier, on every surface (badge, report header, compare, extension). Normal has none. */
export const TIER_EMOJI: Record<Tier, string> = { legendary: '🚀', viral: '🔥', outlier: '⚡', above: '↑', normal: '' };

/** The tier's emoji for a score ('' when there is no score or it is Normal). */
export function tierEmoji(score: number | null | undefined, t: TierThresholds = TIER_DEFAULTS): string {
  const tier = tierFor(score ?? null, t);
  return tier ? TIER_EMOJI[tier] : '';
}

/**
 * The one human-facing Wise Score format (QA 2026-10-16): "84×", "12×", "9.9×", "1.5×", "–" when missing.
 * Truncated, never rounded, so the number shown never reaches a tier it isn't in (9.95 shows "9.9×" next to 🔥, not
 * "10.0×" next to the 🚀 threshold). The tier itself is always decided from the exact score. Exports keep the exact
 * number in their numeric columns; this is for text people read.
 */
export function formatWiseScore(x: number | null | undefined): string {
  if (x === null || x === undefined || !Number.isFinite(x) || x < 0) return '–';
  // A hair of tolerance so 2.3 (stored as 2.29999…) still shows "2.3×".
  if (x >= 10) return `${Math.floor(x + 1e-9).toLocaleString('en-US')}×`;
  return `${(Math.floor(x * 10 + 1e-9) / 10).toFixed(1)}×`;
}

/** "🚀 12×", "9.9×" for Normal, "–" when missing: the tier's own emoji, never a fixed one. */
export function wiseScoreLabel(x: number | null | undefined, t: TierThresholds = TIER_DEFAULTS): string {
  const text = formatWiseScore(x);
  if (text === '–') return text;
  const e = tierEmoji(x, t);
  return e ? `${e} ${text}` : text;
}
